import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormHelperText
} from '@mui/material';
import { Close, Save, CloudUpload, Delete, Place } from '@mui/icons-material';
import { GoogleMap, Polygon, Polyline, Marker } from '@react-google-maps/api';
import { createParkingAsync, updateParkingAsync, uploadParkingPhotoAsync } from '../../redux/slices/parkingsSlice';
import { selectManagementCompanies } from '../../redux/slices/managementCompaniesSlice';
import { darkMapStyles } from '../../resources/constants';
import { Parking, ParkingFormData } from '../../resources/types/parkingsTypes';

interface ParkingModalProps {
  open: boolean;
  onClose: () => void;
  parking: Parking | null;
  mapCenter: { lat: number; lng: number };
}

const PARKING_COLOR = '#4ECDC4';

const ParkingModal: React.FC<ParkingModalProps> = ({ open, onClose, parking, mapCenter }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const managementCompaniesState = useSelector(selectManagementCompanies);
  
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [instructionsEn, setInstructionsEn] = useState('');
  const [instructionsUa, setInstructionsUa] = useState('');
  const [instructionsDe, setInstructionsDe] = useState('');
  const [instructionsEs, setInstructionsEs] = useState('');
  const [photo, setPhoto] = useState<string>('');
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [photoDeleted, setPhotoDeleted] = useState<boolean>(false);
  const [coordinates, setCoordinates] = useState<google.maps.LatLng[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [cursorPosition, setCursorPosition] = useState<google.maps.LatLng | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [errors, setErrors] = useState<{ name?: string; coordinates?: string }>({});
  const [managementCompany, setManagementCompany] = useState<string>('');
  const [commissionPercent, setCommissionPercent] = useState<string>('');
  const [useCustomCommission, setUseCustomCommission] = useState(false);
  const [expandZoneMeters, setExpandZoneMeters] = useState<string>('');

  const mapRef = useRef<google.maps.Map | null>(null);
  const polygonRef = useRef<google.maps.Polygon | null>(null);

  useEffect(() => {
    if (open) {
      if (parking) {
        setName(parking.name);
        setAddress(parking.address || '');
        setInstructionsEn(parking.instructions?.en || '');
        setInstructionsUa(parking.instructions?.ua || '');
        setInstructionsDe(parking.instructions?.de || '');
        setInstructionsEs(parking.instructions?.es || '');
        setPhoto(parking.photo || '');
        setPhotoPreview(parking.photo ? `https://whee.pro/public/${parking.photo}` : '');
        setPhotoDeleted(false);
        setCoordinates(
          parking.coordinates.map(coord => new google.maps.LatLng(coord[0], coord[1]))
        );
        setIsDrawing(false);
        setManagementCompany(parking.managementCompany || '');
        setCommissionPercent(parking.commissionPercent != null ? String(parking.commissionPercent) : '');
        setUseCustomCommission(parking.commissionPercent != null);
        setExpandZoneMeters(parking.expandZoneMeters != null ? String(parking.expandZoneMeters) : '');
      } else {
        setName('');
        setAddress('');
        setInstructionsEn('');
        setInstructionsUa('');
        setInstructionsDe('');
        setInstructionsEs('');
        setPhoto('');
        setPhotoPreview('');
        setPhotoDeleted(false);
        setCoordinates([]);
        setIsDrawing(true);
        setManagementCompany('');
        setCommissionPercent('');
        setUseCustomCommission(false);
        setExpandZoneMeters('');
      }
      setErrors({});
      setTabIndex(0);
    }
  }, [open, parking]);

  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (!isDrawing || !event.latLng) return;
    setCoordinates(prev => [...prev, event.latLng!]);
  }, [isDrawing]);

  const handleMapMouseMove = useCallback((event: google.maps.MapMouseEvent) => {
    if (!isDrawing || !event.latLng) return;
    setCursorPosition(event.latLng);
  }, [isDrawing]);

  const handleFinishDrawing = useCallback(() => {
    if (coordinates.length >= 3) {
      setIsDrawing(false);
      setCursorPosition(null);
    }
  }, [coordinates.length]);

  const handleClearPolygon = useCallback(() => {
    setCoordinates([]);
    setIsDrawing(true);
    setCursorPosition(null);
  }, []);

  const handlePhotoUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log('Starting photo upload for file:', file.name, file.size);

    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const uploadedPath = await uploadParkingPhotoAsync(file);
      console.log('Uploaded photo path:', uploadedPath);
      setUploading(false);

      if (uploadedPath) {
        setPhoto(uploadedPath);
        console.log('Photo state set to:', uploadedPath);
      } else {
        console.error('Upload returned null/empty path');
      }
    } catch (error) {
      console.error('Photo upload failed:', error);
      setUploading(false);
    }
  }, []);

  const handleRemovePhoto = useCallback(() => {
    setPhoto('');
    setPhotoPreview('');
    setPhotoDeleted(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const validate = useCallback(() => {
    const newErrors: { name?: string; coordinates?: string } = {};
    
    if (!name.trim()) {
      newErrors.name = 'Назва обов\'язкова';
    }
    
    if (coordinates.length < 3) {
      newErrors.coordinates = 'Необхідно мінімум 3 точки для створення полігону';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, coordinates]);

  const handleSave = useCallback(async () => {
    if (!validate()) return;

    setSaving(true);

    // Ensure polygon is closed (first and last points must be the same)
    const coords = coordinates.map(coord => [coord.lat(), coord.lng()] as [number, number]);
    if (coords.length >= 3) {
      const first = coords[0];
      const last = coords[coords.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        coords.push([first[0], first[1]]);
      }
    }

    const formData: ParkingFormData = {
      name: name.trim(),
      coordinates: coords,
    };

    if (address.trim()) {
      formData.address = address.trim();
    }

    if (photo) {
      formData.photo = photo;
    } else if (photoDeleted) {
      formData.photo = null;
    }

    if (instructionsEn.trim() || instructionsUa.trim() || instructionsDe.trim() || instructionsEs.trim()) {
      formData.instructions = {};
      if (instructionsEn.trim()) formData.instructions.en = instructionsEn.trim();
      if (instructionsUa.trim()) formData.instructions.ua = instructionsUa.trim();
      if (instructionsDe.trim()) formData.instructions.de = instructionsDe.trim();
      if (instructionsEs.trim()) formData.instructions.es = instructionsEs.trim();
    }

    if (managementCompany) {
      formData.managementCompany = managementCompany;
    }

    if (useCustomCommission && commissionPercent) {
      formData.commissionPercent = commissionPercent;
    } else {
      formData.commissionPercent = null;
    }

    formData.expandZoneMeters = expandZoneMeters !== '' ? Number(expandZoneMeters) : null;

    if (parking) {
      dispatch(updateParkingAsync(parking._id, formData, () => {
        setSaving(false);
        onClose();
      }));
    } else {
      dispatch(createParkingAsync(formData, () => {
        setSaving(false);
        onClose();
      }));
    }
  }, [validate, name, coordinates, address, photo, photoDeleted, instructionsEn, instructionsUa, instructionsDe, instructionsEs, parking, dispatch, onClose, managementCompany, commissionPercent, useCustomCommission, expandZoneMeters]);

  const getDrawingPath = () => {
    if (!isDrawing || coordinates.length === 0) return [];
    const path = [...coordinates];
    if (cursorPosition) {
      path.push(cursorPosition);
    }
    return path;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Place sx={{ color: '#4ECDC4' }} />
          <Typography variant="h6">
            {parking ? 'Редагування парковки' : 'Нова парковка'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs 
          value={tabIndex} 
          onChange={(_, v) => setTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Основна інформація" />
          <Tab label="Зона на карті" />
        </Tabs>

        {tabIndex === 0 && (
          <Box sx={{ p: 3, overflow: 'auto', flex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Назва парковки"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name}
              />

              <TextField
                label="Адреса"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                fullWidth
              />

              <TextField
                label="Розширення зони (метри)"
                value={expandZoneMeters}
                onChange={(e) => setExpandZoneMeters(e.target.value)}
                type="number"
                fullWidth
                inputProps={{ min: 0, step: 1 }}
                helperText="Кількість метрів для розширення зони парковки (null = не розширювати)"
                InputProps={{
                  endAdornment: <InputAdornment position="end">м</InputAdornment>
                }}
              />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Управляюча компанія
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel>Компанія</InputLabel>
                    <Select
                      value={managementCompany}
                      onChange={(e) => setManagementCompany(e.target.value)}
                      label="Компанія"
                    >
                      <MenuItem value="">
                        <em>Не вибрано</em>
                      </MenuItem>
                      {managementCompaniesState.companies.map((company) => (
                        <MenuItem key={company._id} value={company._id}>
                          {company.name} ({company.commissionPercent}%)
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Оберіть управляючу компанію для цієї парковки
                    </FormHelperText>
                  </FormControl>
                  <TextField
                    label="Комісія"
                    value={commissionPercent}
                    onChange={(e) => {
                      setCommissionPercent(e.target.value);
                      setUseCustomCommission(e.target.value !== '');
                    }}
                    type="number"
                    sx={{ width: 150 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    helperText={useCustomCommission ? 'Кастомна комісія' : 'За замовчуванням'}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Фото парковки
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  {photoPreview ? (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={photoPreview}
                        alt="Parking preview"
                        sx={{
                          width: 200,
                          height: 150,
                          objectFit: 'cover',
                          borderRadius: 2
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={handleRemovePhoto}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: '#fff',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      sx={{ height: 100, width: 200 }}
                    >
                      {uploading ? 'Завантаження...' : 'Завантажити фото'}
                    </Button>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePhotoUpload}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Інструкції для користувачів
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Інструкції (EN)"
                    value={instructionsEn}
                    onChange={(e) => setInstructionsEn(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                  />
                  <TextField
                    label="Інструкції (UA)"
                    value={instructionsUa}
                    onChange={(e) => setInstructionsUa(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                  />
                  <TextField
                    label="Інструкції (DE)"
                    value={instructionsDe}
                    onChange={(e) => setInstructionsDe(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                  />
                  <TextField
                    label="Інструкції (ES)"
                    value={instructionsEs}
                    onChange={(e) => setInstructionsEs(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {tabIndex === 1 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', gap: 2 }}>
              {isDrawing ? (
                <>
                  <Alert severity="info" sx={{ flex: 1 }}>
                    Клікайте на карту, щоб додати точки полігону. Мінімум 3 точки.
                    {coordinates.length > 0 && ` Додано точок: ${coordinates.length}`}
                  </Alert>
                  <Button
                    variant="contained"
                    onClick={handleFinishDrawing}
                    disabled={coordinates.length < 3}
                    sx={{ backgroundColor: '#4ECDC4', '&:hover': { backgroundColor: '#3BA99F' } }}
                  >
                    Завершити
                  </Button>
                </>
              ) : (
                <>
                  <Typography sx={{ flex: 1 }}>
                    Полігон створено ({coordinates.length} точок)
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleClearPolygon}
                    startIcon={<Delete />}
                  >
                    Очистити
                  </Button>
                </>
              )}
            </Box>
            {errors.coordinates && (
              <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                {errors.coordinates}
              </Alert>
            )}
            <Box sx={{ flex: 1 }}>
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%', minHeight: 400 }}
                center={mapCenter}
                zoom={15}
                onClick={handleMapClick}
                onMouseMove={handleMapMouseMove}
                options={{
                  styles: theme.palette.mode === 'dark' ? darkMapStyles : undefined,
                  disableDefaultUI: false,
                  zoomControl: true,
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: true,
                  draggableCursor: isDrawing ? 'crosshair' : 'default'
                }}
              >
                {!isDrawing && coordinates.length >= 3 && (
                  <Polygon
                    paths={coordinates}
                    options={{
                      fillColor: PARKING_COLOR,
                      fillOpacity: 0.3,
                      strokeColor: PARKING_COLOR,
                      strokeOpacity: 1,
                      strokeWeight: 2,
                      editable: true
                    }}
                    onLoad={(polygon) => {
                      polygonRef.current = polygon;
                    }}
                    onMouseUp={() => {
                      if (polygonRef.current) {
                        const path = polygonRef.current.getPath();
                        const newCoords: google.maps.LatLng[] = [];
                        for (let i = 0; i < path.getLength(); i++) {
                          newCoords.push(path.getAt(i));
                        }
                        setCoordinates(newCoords);
                      }
                    }}
                    onDragEnd={() => {
                      if (polygonRef.current) {
                        const path = polygonRef.current.getPath();
                        const newCoords: google.maps.LatLng[] = [];
                        for (let i = 0; i < path.getLength(); i++) {
                          newCoords.push(path.getAt(i));
                        }
                        setCoordinates(newCoords);
                      }
                    }}
                  />
                )}

                {isDrawing && coordinates.length > 0 && (
                  <>
                    <Polyline
                      path={getDrawingPath()}
                      options={{
                        strokeColor: PARKING_COLOR,
                        strokeOpacity: 1,
                        strokeWeight: 2
                      }}
                    />
                    {coordinates.map((coord, index) => (
                      <Marker
                        key={index}
                        position={coord}
                        icon={{
                          path: google.maps.SymbolPath.CIRCLE,
                          scale: 6,
                          fillColor: PARKING_COLOR,
                          fillOpacity: 1,
                          strokeColor: '#fff',
                          strokeWeight: 2
                        }}
                      />
                    ))}
                  </>
                )}
              </GoogleMap>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} startIcon={<Close />}>
          Скасувати
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={(saving || uploading) ? <CircularProgress size={20} color="inherit" /> : <Save />}
          disabled={saving || uploading}
          sx={{ backgroundColor: '#4ECDC4', '&:hover': { backgroundColor: '#3BA99F' } }}
        >
          {uploading ? 'Завантаження фото...' : saving ? 'Збереження...' : 'Зберегти'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ParkingModal;
